import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Plus,
  Trash2,
  ArrowLeft,
  Upload,
  Image,
  Film,
  Clock,
  Loader2,
  Check,
  AlertCircle,
  Play,
  X,
  FolderPlus,
  Edit3,
  Save,
  RefreshCw,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import Header from "../components/Header";

const API = "/api";

export default function ManagePage() {
  const [series, setSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesDetail, setSeriesDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState("");
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", season: "", episode: "" });
  const [renamingSeries, setRenamingSeries] = useState(false);
  const [newSeriesNameInput, setNewSeriesNameInput] = useState("");
  const [syncingAll, setSyncingAll] = useState(false);

  const videoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const fetchSeries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/series`);
      setSeries(response.data);
    } catch (err) {
      setMessage({ type: "error", text: "Erreur chargement des séries" });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSeriesDetail = useCallback(async (name) => {
    try {
      const response = await axios.get(`${API}/series/${name}`);
      setSeriesDetail(response.data);
    } catch (err) {
      setMessage({ type: "error", text: "Erreur chargement détails" });
    }
  }, []);

  useEffect(() => { fetchSeries(); }, [fetchSeries]);
  useEffect(() => {
    if (selectedSeries) fetchSeriesDetail(selectedSeries);
    else setSeriesDetail(null);
  }, [selectedSeries, fetchSeriesDetail]);

  const syncAllSeries = async () => {
    setSyncingAll(true);
    try {
      const response = await axios.post(`${API}/scan-all`);
      const { total_added, total_updated, total_failed, message: msg } = response.data;
      if (total_failed > 0) setMessage({ type: "error", text: msg });
      else if (total_added > 0 || total_updated > 0) setMessage({ type: "success", text: msg });
      else setMessage({ type: "success", text: "Toutes les séries sont déjà à jour" });
      await fetchSeries();
      if (selectedSeries) await fetchSeriesDetail(selectedSeries);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur lors de la synchronisation" });
    } finally {
      setSyncingAll(false);
    }
  };

  const createSeries = async () => {
    if (!newSeriesName.trim()) return;
    setCreating(true);
    try {
      const response = await axios.post(`${API}/series/create`, { name: newSeriesName });
      setMessage({ type: "success", text: `Série "${response.data.name}" créée` });
      setNewSeriesName("");
      setShowNewForm(false);
      await fetchSeries();
      setSelectedSeries(response.data.name);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur création" });
    } finally {
      setCreating(false);
    }
  };

  const deleteSeries = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/series/${deleteTarget}`);
      setMessage({ type: "success", text: `Série "${deleteTarget}" supprimée` });
      if (selectedSeries === deleteTarget) setSelectedSeries(null);
      await fetchSeries();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur suppression" });
    } finally {
      setDeleteTarget(null);
    }
  };

  const deleteVideo = async (filename) => {
    if (!selectedSeries) return;
    try {
      await axios.delete(`${API}/video?series=${selectedSeries}&filename=${encodeURIComponent(filename)}`);
      setMessage({ type: "success", text: "Vidéo supprimée" });
      await fetchSeriesDetail(selectedSeries);
      await fetchSeries();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur suppression" });
    }
  };

  const renameSeries = async () => {
    if (!selectedSeries || !newSeriesNameInput.trim()) return;
    try {
      const response = await axios.put(`${API}/series/${selectedSeries}/rename`, { new_name: newSeriesNameInput });
      setMessage({ type: "success", text: `Série renommée en "${response.data.new_name}"` });
      setRenamingSeries(false);
      setSelectedSeries(response.data.new_name);
      await fetchSeries();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur renommage" });
    }
  };

  const startRenameSeries = () => {
    setNewSeriesNameInput(selectedSeries.replace(/_/g, " "));
    setRenamingSeries(true);
  };

  const updateVideoMetadata = async () => {
    if (!selectedSeries || !editingVideo) return;
    try {
      await axios.put(`${API}/series/${selectedSeries}/video`, {
        filename: editingVideo,
        title: editForm.title || null,
        season: editForm.season ? parseInt(editForm.season, 10) : null,
        episode: editForm.episode ? parseInt(editForm.episode, 10) : null,
      });
      setMessage({ type: "success", text: "Métadonnées mises à jour" });
      setEditingVideo(null);
      await fetchSeriesDetail(selectedSeries);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur mise à jour" });
    }
  };

  const scanSeries = async () => {
    if (!selectedSeries) return;
    setScanning(true);
    try {
      const response = await axios.post(`${API}/series/${selectedSeries}/scan`);
      const { added, updated, failed, message: msg } = response.data;
      if (failed.length > 0) setMessage({ type: "error", text: `${msg}. Fichiers en erreur: ${failed.join(", ")}` });
      else if (added.length > 0 || updated.length > 0) setMessage({ type: "success", text: msg });
      else setMessage({ type: "success", text: "Tous les fichiers sont déjà à jour" });
      await fetchSeriesDetail(selectedSeries);
      await fetchSeries();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur lors du scan" });
    } finally {
      setScanning(false);
    }
  };

  const startEditVideo = (video) => {
    setEditingVideo(video.filename);
    setEditForm({
      title: video.title || "",
      season: video.season?.toString() || "",
      episode: video.episode?.toString() || "",
    });
  };

  const parseFilename = (filename) => {
    const name = filename.replace(/\.mp4$/i, "");
    const matchStart = name.match(/^[Ss](\d{1,2})[Ee](\d{1,2})\s*[-_.]?\s*(.*)$/);
    if (matchStart) {
      const title = matchStart[3].trim().replace(/\s*\[.*?\]\s*$/g, "").trim();
      return { season: parseInt(matchStart[1], 10), episode: parseInt(matchStart[2], 10), title: title || null };
    }
    const matchAnywhere = name.match(/[Ss](\d{1,2})[Ee](\d{1,2})/);
    if (matchAnywhere) {
      const afterPattern = name.substring(name.indexOf(matchAnywhere[0]) + matchAnywhere[0].length);
      const title = afterPattern.replace(/^\s*[-_.:]\s*/, "").replace(/\s*\[.*?\]\s*$/g, "").trim();
      return { season: parseInt(matchAnywhere[1], 10), episode: parseInt(matchAnywhere[2], 10), title: title || null };
    }
    const epMatch = name.match(/[Ee](?:p|pisode)?\s*(\d{1,3})/);
    if (epMatch) {
      const afterPattern = name.substring(name.indexOf(epMatch[0]) + epMatch[0].length);
      const title = afterPattern.replace(/^\s*[-_.:]\s*/, "").replace(/\s*\[.*?\]\s*$/g, "").trim();
      return { season: null, episode: parseInt(epMatch[1], 10), title: title || null };
    }
    const title = name.replace(/\s*\[.*?\]\s*$/g, "").trim();
    return { season: null, episode: null, title: title || null };
  };

  const getVideoDuration = (file) => new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(Math.round(video.duration));
    };
    video.onerror = () => resolve(0);
    video.src = URL.createObjectURL(file);
  });

  const handleVideoFiles = async (files) => {
    const validFiles = Array.from(files).filter((f) => f.type === "video/mp4" || f.name.toLowerCase().endsWith(".mp4"));
    if (validFiles.length === 0) {
      setMessage({ type: "error", text: "Sélectionnez des fichiers .mp4" });
      return;
    }

    const filesWithDuration = await Promise.all(validFiles.map(async (file) => {
      const parsed = parseFilename(file.name);
      return {
        file,
        name: file.name,
        size: file.size,
        duration: await getVideoDuration(file),
        title: parsed.title || "",
        season: parsed.season?.toString() || "",
        episode: parsed.episode?.toString() || "",
        status: "pending",
        progress: 0,
      };
    }));

    setUploadQueue((prev) => [...prev, ...filesWithDuration]);
  };

  const updateQueueItem = (idx, field, value) => {
    setUploadQueue((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const uploadVideos = async () => {
    if (!selectedSeries || uploadQueue.length === 0) return;
    setUploading(true);

    for (let i = 0; i < uploadQueue.length; i += 1) {
      const item = uploadQueue[i];
      if (item.status === "done") continue;
      setUploadQueue((prev) => prev.map((f, idx) => idx === i ? { ...f, status: "uploading", progress: 0 } : f));
      try {
        const formData = new FormData();
        formData.append("file", item.file);
        formData.append("series", selectedSeries);
        formData.append("duration", item.duration);
        if (item.title) formData.append("title", item.title);
        if (item.season) formData.append("season", parseInt(item.season, 10));
        if (item.episode) formData.append("episode", parseInt(item.episode, 10));

        await axios.post(`${API}/upload`, formData, {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadQueue((prev) => prev.map((f, idx) => idx === i ? { ...f, progress: percentCompleted } : f));
          },
        });
        setUploadQueue((prev) => prev.map((f, idx) => idx === i ? { ...f, status: "done", progress: 100 } : f));
      } catch (err) {
        setUploadQueue((prev) => prev.map((f, idx) => idx === i ? { ...f, status: "error" } : f));
      }
    }

    setUploading(false);
    setUploadQueue([]);
    await fetchSeriesDetail(selectedSeries);
    await fetchSeries();
    setMessage({ type: "success", text: "Upload terminé" });
  };

  const uploadCover = async (file) => {
    if (!selectedSeries || !file) return;
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);
      await axios.post(`${API}/series/${selectedSeries}/cover`, formData);
      setMessage({ type: "success", text: "Image de couverture mise à jour" });
      await fetchSeriesDetail(selectedSeries);
      await fetchSeries();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur upload image" });
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const placeholderImages = [
    "https://images.unsplash.com/photo-1639749601642-8d30474f249f?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1566750859749-5aa92e0b4872?w=400&h=300&fit=crop",
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header showManageLink={false} />

      <div className="border-b border-white/10 p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="back-to-home" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase tracking-tighter">Gestion des playlists</h1>
          </div>
          <button
            data-testid="sync-all-btn"
            onClick={syncAllSeries}
            disabled={syncingAll}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-white text-sm font-medium hover:bg-white/20 disabled:opacity-50 transition-colors"
          >
            {syncingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden sm:inline">Tout synchroniser</span>
          </button>
        </div>
      </div>

      {message && (
        <div className={`mx-4 md:mx-6 mt-4 p-4 flex items-center justify-between ${message.type === "error" ? "bg-red-500/10 border border-red-500/30 text-red-400" : "bg-green-500/10 border border-green-500/30 text-green-400"}`}>
          <div className="flex items-center gap-3">
            {message.type === "error" ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {message.text}
          </div>
          <button onClick={() => setMessage(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-white/10 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-semibold">Séries</h2>
            <button data-testid="new-series-btn" onClick={() => setShowNewForm(true)} className="flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors">
              <Plus className="w-4 h-4" />Nouvelle
            </button>
          </div>

          {showNewForm && (
            <div className="mb-4 p-4 border border-white/20 bg-white/5">
              <input
                type="text"
                data-testid="new-series-input"
                placeholder="Nom de la série"
                value={newSeriesName}
                onChange={(e) => setNewSeriesName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createSeries()}
                className="w-full px-3 py-2 mb-3 bg-black border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none text-sm"
                autoFocus
              />
              <div className="flex gap-2">
                <button data-testid="create-series-btn" onClick={createSeries} disabled={creating} className="flex-1 px-3 py-2 bg-white text-black font-bold text-xs uppercase tracking-wider hover:bg-gray-200 disabled:opacity-50 transition-colors">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Créer"}
                </button>
                <button onClick={() => { setShowNewForm(false); setNewSeriesName(""); }} className="px-3 py-2 border border-white/20 text-white/60 hover:text-white text-xs transition-colors">Annuler</button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div>
          ) : series.length === 0 ? (
            <div className="text-center py-8">
              <FolderPlus className="w-12 h-12 mx-auto mb-3 text-white/20" />
              <p className="text-white/40 text-sm">Aucune série</p>
              <p className="text-white/30 text-xs mt-1">Créez votre première playlist</p>
            </div>
          ) : (
            <div className="space-y-2">
              {series.map((s, index) => (
                <div
                  key={s.name}
                  data-testid={`series-item-${s.name}`}
                  className={`group relative flex items-center gap-3 p-3 cursor-pointer transition-colors ${selectedSeries === s.name ? "bg-white/10 border border-white/30" : "border border-transparent hover:bg-white/5"}`}
                  onClick={() => setSelectedSeries(s.name)}
                >
                  <div className="w-16 h-12 bg-white/10 overflow-hidden flex-shrink-0">
                    <img src={s.cover_image || placeholderImages[index % 2]} alt={s.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{s.name.replace(/_/g, " ")}</p>
                    <p className="text-white/50 text-xs">{s.video_count} vidéo{s.video_count > 1 ? "s" : ""}</p>
                  </div>
                  <button data-testid={`delete-series-${s.name}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget(s.name); }} className="opacity-0 group-hover:opacity-100 p-2 text-white/40 hover:text-red-400 transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 p-4 md:p-6 overflow-auto">
          {!selectedSeries ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Film className="w-16 h-16 text-white/20 mb-4" />
              <p className="text-white/40">Sélectionnez une série pour la modifier</p>
              <p className="text-white/30 text-sm mt-1">ou créez-en une nouvelle</p>
            </div>
          ) : (
            <div>
              <div className="flex flex-col md:flex-row gap-6 mb-8">
                <div className="relative w-full md:w-64 aspect-video bg-white/10 overflow-hidden group cursor-pointer" onClick={() => coverInputRef.current?.click()}>
                  <img src={seriesDetail?.cover_image ? `${seriesDetail.cover_image}?t=${Date.now()}` : placeholderImages[0]} alt="Cover" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 md:flex items-center justify-center transition-opacity hidden">
                    <div className="text-center"><Image className="w-8 h-8 text-white mx-auto mb-2" /><span className="text-white text-sm">Changer la couverture</span></div>
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) { uploadCover(file); e.target.value = ""; }
                    }}
                    className="hidden"
                  />
                </div>

                <div className="flex-1">
                  {renamingSeries ? (
                    <div className="mb-4">
                      <input type="text" value={newSeriesNameInput} onChange={(e) => setNewSeriesNameInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && renameSeries()} className="w-full px-3 py-2 bg-black border border-white/20 text-white text-xl font-bold focus:border-white focus:outline-none mb-2" autoFocus />
                      <div className="flex gap-2">
                        <button onClick={renameSeries} className="px-3 py-1 bg-white text-black font-bold text-xs uppercase tracking-wider hover:bg-gray-200"><Save className="w-3 h-3 inline mr-1" />Sauver</button>
                        <button onClick={() => setRenamingSeries(false)} className="px-3 py-1 border border-white/20 text-white/60 text-xs hover:text-white">Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 mb-2 group">
                      <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight">{selectedSeries.replace(/_/g, " ")}</h2>
                      <button onClick={startRenameSeries} className="opacity-0 group-hover:opacity-100 p-1 text-white/40 hover:text-white transition-all" title="Renommer"><Edit3 className="w-4 h-4" /></button>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-white/50 text-sm mb-4">
                    <span className="flex items-center gap-1"><Film className="w-4 h-4" />{seriesDetail?.videos?.length || 0} vidéos</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatDuration(seriesDetail?.total_duration || 0)} total</span>
                  </div>
                  <Link to={`/watch/${selectedSeries}`} data-testid="watch-series-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black font-bold text-sm uppercase tracking-wider hover:bg-gray-200 transition-colors"><Play className="w-4 h-4" />Regarder</Link>
                </div>
              </div>

              <div className="mb-6">
                <div data-testid="video-drop-zone" onClick={() => videoInputRef.current?.click()} className="border-2 border-dashed border-white/20 p-8 text-center cursor-pointer hover:border-white/40 transition-colors mb-4">
                  <Upload className="w-10 h-10 mx-auto mb-3 text-white/40" />
                  <p className="text-white/60 mb-1">Glissez vos vidéos .mp4 ici</p>
                  <p className="text-white/40 text-sm">ou cliquez pour sélectionner</p>
                </div>
                <button data-testid="scan-series-btn" onClick={scanSeries} disabled={scanning} className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm transition-colors disabled:opacity-50">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Scanner les fichiers manuels
                </button>
                <input ref={videoInputRef} type="file" accept="video/mp4,.mp4" multiple onChange={(e) => handleVideoFiles(e.target.files)} className="hidden" />
              </div>

              {uploadQueue.length > 0 && (
                <div className="mb-6 p-4 border border-white/10 bg-white/5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white font-medium">{uploadQueue.length} fichier{uploadQueue.length > 1 ? "s" : ""} à uploader</span>
                    <button data-testid="upload-videos-btn" onClick={uploadVideos} disabled={uploading} className="flex items-center gap-2 px-4 py-2 bg-white text-black font-bold text-xs uppercase tracking-wider hover:bg-gray-200 disabled:opacity-50 transition-colors">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Uploader
                    </button>
                  </div>
                  <div className="space-y-3">
                    {uploadQueue.map((item, idx) => (
                      <div key={`${item.name}-${idx}`} className="p-3 border border-white/10 bg-black/30">
                        <div className="flex items-center gap-3 mb-2">
                          <Film className="w-4 h-4 text-white/40 flex-shrink-0" />
                          <span className="flex-1 text-white/70 truncate text-sm">{item.name}</span>
                          <span className="text-white/40 text-xs">{formatDuration(item.duration)}</span>
                          {item.status === "done" && <Check className="w-4 h-4 text-green-400" />}
                          {item.status === "uploading" && <span className="text-blue-400 text-xs font-mono">{item.progress}%</span>}
                          {item.status === "error" && <AlertCircle className="w-4 h-4 text-red-400" />}
                          {item.status === "pending" && <button onClick={() => setUploadQueue((prev) => prev.filter((_, i) => i !== idx))} className="text-white/40 hover:text-red-400"><X className="w-4 h-4" /></button>}
                        </div>
                        {item.status === "uploading" && <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-2"><div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${item.progress}%` }} /></div>}
                        {item.status === "pending" && (
                          <div className="grid grid-cols-3 gap-2">
                            <input type="text" placeholder="Titre épisode" value={item.title} onChange={(e) => updateQueueItem(idx, "title", e.target.value)} className="px-2 py-1 bg-black border border-white/20 text-white text-xs placeholder-white/30" />
                            <input type="number" placeholder="Saison" value={item.season} onChange={(e) => updateQueueItem(idx, "season", e.target.value)} className="px-2 py-1 bg-black border border-white/20 text-white text-xs placeholder-white/30" />
                            <input type="number" placeholder="Épisode" value={item.episode} onChange={(e) => updateQueueItem(idx, "episode", e.target.value)} className="px-2 py-1 bg-black border border-white/20 text-white text-xs placeholder-white/30" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-white font-semibold mb-3">Vidéos dans la playlist</h3>
                <p className="text-white/40 text-xs mb-4">Triées par Saison → Épisode → Nom de fichier</p>
                {!seriesDetail?.videos?.length ? (
                  <div className="text-center py-8 border border-white/10 bg-white/5"><Film className="w-10 h-10 mx-auto mb-2 text-white/20" /><p className="text-white/40 text-sm">Aucune vidéo</p></div>
                ) : (
                  <div className="space-y-2">
                    {seriesDetail.videos.map((video, idx) => (
                      <div key={video.filename} data-testid={`video-item-${idx}`} className="border border-white/10 bg-white/5 group">
                        {editingVideo === video.filename ? (
                          <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                              <input type="text" placeholder="Titre de l'épisode" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="md:col-span-2 px-3 py-2 bg-black border border-white/20 text-white text-sm" />
                              <input type="number" placeholder="Saison" value={editForm.season} onChange={(e) => setEditForm({ ...editForm, season: e.target.value })} className="px-3 py-2 bg-black border border-white/20 text-white text-sm" />
                              <input type="number" placeholder="Épisode" value={editForm.episode} onChange={(e) => setEditForm({ ...editForm, episode: e.target.value })} className="px-3 py-2 bg-black border border-white/20 text-white text-sm" />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={updateVideoMetadata} className="flex items-center gap-2 px-3 py-1 bg-white text-black font-bold text-xs uppercase tracking-wider"><Save className="w-3 h-3" />Sauvegarder</button>
                              <button onClick={() => setEditingVideo(null)} className="px-3 py-1 border border-white/20 text-white/60 text-xs">Annuler</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-4 p-4">
                            <span className="w-8 h-8 flex items-center justify-center bg-white/10 text-white/60 text-sm font-mono flex-shrink-0">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {(video.season || video.episode) && <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold flex-shrink-0">{video.season ? `S${video.season.toString().padStart(2, "0")}` : ""}{video.episode ? `E${video.episode.toString().padStart(2, "0")}` : ""}</span>}
                                <p className="text-white truncate">{video.title || <span className="text-white/40 italic">Sans titre</span>}</p>
                              </div>
                              <p className="text-white/40 text-xs truncate mt-1">{video.filename}</p>
                              <p className="text-white/50 text-sm">{formatDuration(video.duration)}</p>
                            </div>
                            <button onClick={() => startEditVideo(video)} className="opacity-0 group-hover:opacity-100 p-2 text-white/40 hover:text-white transition-all" title="Modifier"><Edit3 className="w-4 h-4" /></button>
                            <button data-testid={`delete-video-${idx}`} onClick={() => deleteVideo(video.filename)} className="opacity-0 group-hover:opacity-100 p-2 text-white/40 hover:text-red-400 transition-all" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer la série ?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Cette action supprimera définitivement la série "{deleteTarget}" et toutes ses vidéos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 text-white hover:bg-white/10">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSeries} className="bg-red-600 text-white hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
